import { RootState } from '@/redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { dynamicButtons } from '@/constants/landing';
import { resolveNextAuthStep } from '@/lib/auth/next-step';
import { IdentityStatus } from './auth.type';

const authState = () => (state: RootState) => state.auth;

export const selectAccessToken = createSelector(
  authState(),
  (auth) => auth.accessToken
);

export const selectUserInfo = createSelector(authState(), (auth) => auth.user);
export const selectUserStatus = createSelector(authState(), (auth) => ({
  status: auth.user.workIdentityVerificationStatus,
  isFetchingProfile: auth.isFetchingProfile
}));

export const selectIsFullyVerified = createSelector(authState(), (auth) => {
  const status = auth.user.workIdentityVerificationStatus;
  return (
    status === IdentityStatus.VERIFIED ||
    status === IdentityStatus.MANUAL_VERIFIED
  );
});

export const selectProfileCompletionLink = createSelector(
  authState(),
  (auth) => auth.profileCompletionLink
);

export const selectDynamicButton = createSelector(authState(), (auth) => {
  const { user, accessToken, profileCompletionLink } = auth || {};
  const { isMailVerified, workIdentityVerificationStatus } = user || {};

  let dynamicButton: typeof dynamicButtons.becomeMember | null =
    dynamicButtons.becomeMember;

  if (accessToken && user.email) {
    // Destination comes from resolveNextAuthStep so this CTA and the login
    // redirect can't disagree — they used to, and a PENDING user with a work
    // email was sent to a status page here while login sent them to the code
    // screen. Only the copy is decided below.
    const buttonLink = resolveNextAuthStep(user, profileCompletionLink);

    if (!isMailVerified) {
      dynamicButton = { ...dynamicButtons.verifyAccount, buttonLink };
    } else if (
      workIdentityVerificationStatus === IdentityStatus.NOT_INITIATED
    ) {
      // Mail is confirmed already, so the next step is the work identity —
      // which is the profile-completion form, not the account verification the
      // verifyAccount copy describes.
      dynamicButton = { ...dynamicButtons.completeProfile, buttonLink };
    } else if (
      workIdentityVerificationStatus === IdentityStatus.PENDING ||
      workIdentityVerificationStatus ===
        IdentityStatus.MANUAL_VERIFICATION_PENDING
    ) {
      dynamicButton = { ...dynamicButtons.verifyingIdentity, buttonLink };
    } else if (
      workIdentityVerificationStatus === IdentityStatus.REJECTED ||
      workIdentityVerificationStatus === IdentityStatus.REJECTED_AND_REAPPLY
    ) {
      dynamicButton = { ...dynamicButtons.rejectedIdentity, buttonLink };
    } else {
      // Verified users have no funnel step left, so this one keeps its own
      // destination rather than resolveNextAuthStep's home redirect.
      // No email in the URL: /referral never reads it, and a query string lands in CDN, server and analytics logs.
      dynamicButton = { ...dynamicButtons.referAFriend };
    }
  }

  return dynamicButton;
});
